import React, { useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Send } from 'lucide-react';

function ContactItem({ user, conversation, isSelected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(user.id)}
      className={`w-full p-3 flex items-center gap-3 hover:bg-[#2C3038] transition-colors text-left ${isSelected ? 'bg-[#F2EBE1]' : ''}`}
      data-testid={`chat-user-${user.id}`}
    >
      <Avatar className="w-10 h-10">
        <AvatarImage src={user.avatar} />
        <AvatarFallback className="bg-[#4A9D6E] text-white">{user.name?.charAt(0)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[#F0EDE8] truncate">{user.name}</p>
        {conversation && <p className="text-xs text-[#9CA3AF] truncate">{conversation.last_message}</p>}
      </div>
      {conversation && conversation.unread_count > 0 && (
        <Badge className="bg-[#D4855A] text-white rounded-full">{conversation.unread_count}</Badge>
      )}
    </button>
  );
}

function ContactList({ otherUsers, conversations, selectedChat, onOpenChat }) {
  return (
    <div className="w-1/3 border-r border-[#33383F] flex flex-col">
      <div className="p-2">
        <p className="accent-text text-[#9CA3AF] px-2">TEAM</p>
      </div>
      <ScrollArea className="flex-1">
        {otherUsers.map((u) => (
          <ContactItem key={u.id} user={u}
            conversation={conversations.find(c => c.user_id === u.id)}
            isSelected={selectedChat === u.id} onSelect={onOpenChat} />
        ))}
      </ScrollArea>
    </div>
  );
}

function MessageBubble({ msg, isSelf }) {
  return (
    <div className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] px-4 py-2 ${isSelf ? 'message-self' : 'message-other'}`}
        data-testid={`message-${msg.id}`}>
        <p className="text-sm">{msg.content}</p>
        <p className={`text-xs mt-1 ${isSelf ? 'text-white/70' : 'text-[#9CA3AF]'}`}>
          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}

function ChatArea({ selectedChat, otherUsers, messages, newMessage, setNewMessage, onSendMessage, currentUserId }) {
  const chatPartner = useMemo(
    () => otherUsers.find(u => u.id === selectedChat),
    [otherUsers, selectedChat]
  );

  if (!selectedChat) {
    return (
      <div className="flex-1 flex items-center justify-center text-[#9CA3AF]">
        <p>Select a conversation to start messaging</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="p-3 border-b border-[#33383F] flex items-center gap-3">
        <Avatar className="w-8 h-8">
          <AvatarImage src={chatPartner?.avatar} />
          <AvatarFallback className="bg-[#4A9D6E] text-white">{chatPartner?.name?.charAt(0)}</AvatarFallback>
        </Avatar>
        <span className="font-medium text-[#F0EDE8]">{chatPartner?.name}</span>
      </div>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} isSelf={msg.sender_id === currentUserId} />
          ))}
        </div>
      </ScrollArea>
      <form onSubmit={onSendMessage} className="p-3 border-t border-[#33383F] flex gap-2">
        <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 bg-[#1A1D21] border-[#33383F] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] rounded-xl"
          data-testid="message-input" />
        <Button type="submit" className="bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl" data-testid="send-message-button">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

export function MessagesDialog({
  open, onOpenChange, otherUsers, conversations, selectedChat,
  messages, newMessage, setNewMessage, onOpenChat, onSendMessage, currentUserId
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1A1D21] border-[#33383F] rounded-xl max-w-2xl h-[600px] flex flex-col p-0">
        <DialogHeader className="p-4 border-b border-[#33383F]">
          <DialogTitle className="text-[#F0EDE8] font-bold tracking-tight">Messages</DialogTitle>
        </DialogHeader>
        <div className="flex flex-1 overflow-hidden">
          <ContactList otherUsers={otherUsers} conversations={conversations}
            selectedChat={selectedChat} onOpenChat={onOpenChat} />
          <ChatArea selectedChat={selectedChat} otherUsers={otherUsers} messages={messages}
            newMessage={newMessage} setNewMessage={setNewMessage}
            onSendMessage={onSendMessage} currentUserId={currentUserId} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
